import React, { useEffect, useState } from "react";
import { generateComponentUniqueKey } from "../../../../../util/key-generator";
import { Grid, useMediaQuery } from "@mui/material";
import CustomLink from "../../../../../ui/widgets/link";
import Icon, {
  InsurancePlusIcon,
  InvoicingTypeIcon,
  SignatureIcon,
} from "../../../../../ui/widgets/icons";
import { useMessageSource } from "react-message-source";
import { createApplicationPdfLink } from "../../../../../util/pdf";
import { breakpointValues } from "../../../../../theme/theme";
import { ProductRefType } from "../../../../../context/product-context/types";
import { DashboardCardApplicationDocument } from "../../../../../api/commercial/generated-types";
import { useFeatureToggles } from "../../../../shared/hooks/useFeatureToggles";
import { Feature } from "../../../../../context/types";
import { AdditionalInfoDialog } from "./additional-info-dialog";
import MoreTooltip from "../../../../../ui/widgets/table/more-tooltip";
import useInsurancePlus from "../../../../shared/hooks/useInsurancePlus";
import { MessageSourceApi } from "react-message-source/dist/lib/useMessageSource";

export interface CardholderTableRow {
  id: string;
  cardholderName?: string;
  cardholderProductName?: string;
  cardholderProductPdf?: React.JSX.Element;
  cardholderSignature?: React.JSX.Element;
  CardholderProduct?: React.JSX.Element;
  CardholderProductActions?: React.JSX.Element;
  additionalInfo?: React.JSX.Element;
  insurancePlus?: React.JSX.Element;
  invoicingType?: React.JSX.Element;
  productCode: ProductRefType;
}

const largeScreens = `only screen and (min-width: ${breakpointValues.md}px)`;

/**
 * This function will create a single row of a cardholder table data.
 * @param cardApplication The card application.
 * @param getMessage The translation function.
 * @return object The matched link with reference to it's card application.
 */
const getCardholderTableRow = (
  cardApplication: DashboardCardApplicationDocument,
  getMessage: MessageSourceApi["getMessage"],
  isCurrentScreenLarge: boolean,
  canCreateMultipleBaaPerProduct: boolean,
  additionalInfoButton: (
    document: DashboardCardApplicationDocument,
  ) => React.JSX.Element,
  hasInsurancePlus: boolean,
): Promise<CardholderTableRow> => {
  const additionalInfoRowsSmall = canCreateMultipleBaaPerProduct
    ? {
        additionalInfo: additionalInfoButton(cardApplication),
      }
    : {};

  const additionalInfoRowsLarge = canCreateMultipleBaaPerProduct
    ? {
        insurancePlus: hasInsurancePlus && (
          <div className="align-center-content">
            <InsurancePlusIcon />
          </div>
        ),
        invoicingType: cardApplication.hasDecentralizedInvoicing !== null && (
          <div className="align-center-content">
            <InvoicingTypeIcon
              hasDecentralizedInvoicing={
                cardApplication.hasDecentralizedInvoicing
              }
            />
          </div>
        ),
      }
    : {};

  const tableRowsSmall = () => {
    return new Promise<CardholderTableRow>((resolve, reject) => {
      createApplicationPdfLink(cardApplication.applicationRef)
        .then((resolvedCardPdfLink) => {
          resolve({
            id: generateComponentUniqueKey(),
            CardholderProduct: (
              <Grid container>
                <Grid size={{ xs: 12, md: 5 }}>
                  {getMessage(
                    `${cardApplication.cardholderFirstName} ${cardApplication.cardholderLastName}`,
                  )}
                </Grid>
                <Grid className="italic-cell" size={{ xs: 12, md: 6 }}>
                  {getMessage(`Product.${cardApplication.productRef}.title`)}
                </Grid>
              </Grid>
            ),
            // used only for the filter, does not appear in the list of columns, and will not be displayed
            productCode: cardApplication.productRef as ProductRefType,
            CardholderProductActions: (
              <Grid container className="mobile-cell-split">
                <Grid className="scd-align-center" size={{ xs: 6 }}>
                  <CustomLink
                    noicon
                    href={resolvedCardPdfLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Icon icon="icon-scd-pdf" />
                    <span
                      className="item-text"
                      title={getMessage(
                        "Dashboard.cardholderApplicationApplied.pdf-link",
                      )}
                    >
                      {getMessage(
                        "Dashboard.cardholderApplicationApplied.pdf-link",
                      )}
                    </span>
                  </CustomLink>
                </Grid>
                <Grid className="scd-align-center" size={{ xs: 6 }}>
                  <SignatureIcon type={cardApplication.status} />
                  <span
                    className="item-text"
                    title={getMessage(
                      `Dashboard.cardApplication-signature-${cardApplication.status.toLowerCase()}`,
                    )}
                  >
                    {getMessage(
                      `Dashboard.cardApplication-signature-${cardApplication.status.toLowerCase()}`,
                    )}
                  </span>
                </Grid>
              </Grid>
            ),
            ...additionalInfoRowsSmall,
          });
        })
        .catch((e) => reject(e));
    });
  };

  const tableRowsLarge = () => {
    return new Promise<CardholderTableRow>((resolve, reject) => {
      createApplicationPdfLink(cardApplication.applicationRef)
        .then((resolvedCardPdfLink) => {
          resolve({
            id: generateComponentUniqueKey(),
            cardholderName: getMessage(
              `${cardApplication.cardholderFirstName} ${cardApplication.cardholderLastName}`,
            ),

            cardholderProductName: getMessage(
              `Product.${cardApplication.productRef}.title`,
            ),
            // used only for the filter, does not appear in the list of columns, and will not be displayed
            productCode: cardApplication.productRef as ProductRefType,
            cardholderProductPdf: (
              <CustomLink
                noicon
                href={resolvedCardPdfLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                <span
                  className="item-text"
                  title={getMessage(
                    "Dashboard.cardholderApplicationApplied.pdf-link",
                  )}
                >
                  {getMessage(
                    "Dashboard.cardholderApplicationApplied.pdf-link",
                  )}
                </span>
                <Icon icon="icon-scd-pdf" />
              </CustomLink>
            ),
            cardholderSignature: (
              <div className="scd-signature-table">
                <span
                  className="item-text"
                  title={getMessage(
                    `Dashboard.cardApplication-signature-${cardApplication.status.toLowerCase()}`,
                  )}
                >
                  {getMessage(
                    `Dashboard.cardApplication-signature-${cardApplication.status.toLowerCase()}`,
                  )}
                </span>
                <SignatureIcon type={cardApplication.status} />
              </div>
            ),
            ...additionalInfoRowsLarge,
          });
        })
        .catch((e) => reject(e));
    });
  };

  return isCurrentScreenLarge ? tableRowsLarge() : tableRowsSmall();
};

const useCardholderTableBodyRows = (
  cardholderApplications: DashboardCardApplicationDocument[],
): CardholderTableRow[] => {
  const { getMessage } = useMessageSource();
  const { hasInsurancePlus } = useInsurancePlus();

  const canCreateMultipleBaaPerProduct = useFeatureToggles(
    Feature.MULTIPLE_BASIC_ACCOUNT_APPLICATIONS_PER_PRODUCT,
  );
  const [cardholderTableRows, setCardholderTableRows] = useState<
    CardholderTableRow[] | null
  >(null);

  const isCurrentScreenLarge = useMediaQuery(largeScreens, { noSsr: true });

  const additionalInfoButton = (
    cardholderApplication: DashboardCardApplicationDocument,
  ) => {
    return (
      <div className="scd-more-table">
        <MoreTooltip
          key={cardholderApplication.applicationRef}
          title={
            <AdditionalInfoDialog cardApplication={cardholderApplication} />
          }
        >
          <i className="icon-scd-more" />
        </MoreTooltip>
      </div>
    );
  };

  useEffect(() => {
    const run = async (): Promise<void> => {
      const applicationRows = cardholderApplications.map(
        (cardApplication: DashboardCardApplicationDocument) => {
          return getCardholderTableRow(
            cardApplication,
            getMessage,
            isCurrentScreenLarge,
            canCreateMultipleBaaPerProduct,
            additionalInfoButton,
            hasInsurancePlus,
          );
        },
      );
      setCardholderTableRows(await Promise.all(applicationRows));
    };
    run();
  }, [
    canCreateMultipleBaaPerProduct,
    cardholderApplications,
    isCurrentScreenLarge,
    getMessage,
    hasInsurancePlus,
  ]);

  return cardholderTableRows;
};

export interface CardholderTableHeadRows {
  id: keyof CardholderTableRow;
  label: string | React.JSX.Element;
  sortable?: boolean;
}

const useCardholderTableHeadRows = (): CardholderTableHeadRows[] => {
  const { getMessage } = useMessageSource();
  const isCurrentScreenLarge = useMediaQuery(largeScreens);
  const canCreateMultipleBaaPerProduct = useFeatureToggles(
    Feature.MULTIPLE_BASIC_ACCOUNT_APPLICATIONS_PER_PRODUCT,
  );

  const additionalInfoRowsLarge: CardholderTableHeadRows[] =
    canCreateMultipleBaaPerProduct
      ? [
          {
            id: "insurancePlus",
            label: (
              <div className="align-center-text">
                {getMessage(
                  "Dashboard.cardholderApplication.table-insurance-plus",
                )}{" "}
              </div>
            ),
            sortable: false,
          },
          {
            id: "invoicingType",
            label: (
              <div className="align-center-text">
                {getMessage(
                  "Dashboard.cardholderApplication.table-invoicing-type",
                )}
              </div>
            ),
            sortable: false,
          },
        ]
      : [];

  const additionalInfoRowsSmall: CardholderTableHeadRows[] =
    canCreateMultipleBaaPerProduct
      ? [
          {
            id: "additionalInfo",
            label: "",
            sortable: false,
          },
        ]
      : [];

  const tableRowsLarge = (): CardholderTableHeadRows[] => {
    return [
      {
        id: "cardholderName",
        label: getMessage("Dashboard.cardholderApplication.table-full-name"),
        sortable: true,
      },
      {
        id: "cardholderProductName",
        label: getMessage("Dashboard.cardholderApplication.table-product"),
        sortable: true,
      },
      {
        id: "cardholderProductPdf",
        label: getMessage("Dashboard.cardholderApplication.table-pdf"),
        sortable: false,
      },
      {
        id: "cardholderSignature",
        label: getMessage("Dashboard.cardholderApplication.table-signature"),
        sortable: false,
      },
      ...additionalInfoRowsLarge,
    ];
  };

  const tableRowsSmall = (): CardholderTableHeadRows[] => {
    return [
      {
        id: "CardholderProduct",
        label: (
          <Grid container>
            <Grid size={{ xs: 12, md: 5 }}>
              {getMessage("Dashboard.cardholderApplication.table-full-name")}
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              {getMessage("Dashboard.cardholderApplication.table-product")}
            </Grid>
          </Grid>
        ),
      },
      {
        id: "CardholderProductActions",
        label: (
          <Grid container>
            <Grid size={{ xs: 12, md: 6 }}>
              {getMessage("Dashboard.cardholderApplication.table-pdf")}
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              {getMessage("Dashboard.cardholderApplication.table-signature")}
            </Grid>
          </Grid>
        ),
      },
      ...additionalInfoRowsSmall,
    ];
  };

  return isCurrentScreenLarge ? tableRowsLarge() : tableRowsSmall();
};

export { useCardholderTableBodyRows, useCardholderTableHeadRows };
